import { useEffect, useRef } from 'react';

/**
 * The Cloudflare Turnstile widget, rendered explicitly.
 *
 * Explicit rather than the automatic `cf-turnstile` class scan, because the widget appears
 * conditionally — under the adaptive mode nothing is drawn until a sign-in comes back
 * asking for it — and the automatic mode only looks at the DOM once, on script load.
 */
interface TurnstileApi {
  render: (
    element: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
      theme?: 'auto' | 'light' | 'dark';
      appearance?: 'always' | 'execute' | 'interaction-only';
    },
  ) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise: Promise<boolean> | null = null;

/** Loaded once per page, however many widgets ask for it. */
function loadScript(): Promise<boolean> {
  if (window.turnstile) return Promise.resolve(true);

  scriptPromise ??= new Promise<boolean>((resolve) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    const script = existing ?? document.createElement('script');

    script.addEventListener('load', () => resolve(true));

    // A blocked or unreachable challenges.cloudflare.com resolves false rather than
    // hanging. The sign-in form stays usable and the server decides what to do with a
    // request that arrives without a token, which is the only place that decision belongs.
    script.addEventListener('error', () => resolve(false));

    if (!existing) {
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });

  return scriptPromise;
}

export interface TurnstileProps {
  siteKey: string;

  /** Called with a fresh token, and with null when one expires or the widget errors. */
  onToken: (token: string | null) => void;

  /**
   * Changing this resets the widget.
   *
   * A Turnstile token is single use and dies with the attempt that spent it, so every
   * submit — successful or not — has to be followed by a reset. Without it a second attempt
   * sends a token the server has already seen and is refused for a reason the person
   * reading the screen cannot do anything about.
   */
  resetKey?: number;
}

export function Turnstile({ siteKey, onToken, resetKey = 0 }: TurnstileProps) {
  const host = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);

  // Held in a ref so that a parent re-render does not tear the widget down and rebuild it,
  // which would drop a token the person has already solved for.
  const emit = useRef(onToken);
  emit.current = onToken;

  useEffect(() => {
    let cancelled = false;

    void loadScript().then((ready) => {
      if (cancelled || !ready || !window.turnstile || !host.current) return;
      if (widgetId.current !== null) return;

      widgetId.current = window.turnstile.render(host.current, {
        sitekey: siteKey,
        callback: (token) => emit.current(token),

        // Turnstile tokens last five minutes. Somebody who solves the challenge and then
        // goes to find their password manager can easily cross that, and clearing the token
        // is what makes the next submit ask for a new one instead of sending a dead one.
        'expired-callback': () => emit.current(null),
        'error-callback': () => emit.current(null),
        theme: 'auto',
      });
    });

    return () => {
      cancelled = true;

      if (widgetId.current !== null && window.turnstile) {
        window.turnstile.remove(widgetId.current);
        widgetId.current = null;
      }
    };
  }, [siteKey]);

  useEffect(() => {
    // Nothing to reset on the first render; only on a later change of the key.
    if (resetKey === 0) return;

    if (widgetId.current !== null && window.turnstile) {
      window.turnstile.reset(widgetId.current);
      emit.current(null);
    }
  }, [resetKey]);

  return <div ref={host} className="flex justify-center" />;
}
